import ErrorSummary from '@/components/ErrorSummary'
import { FormLabel } from '@/components/FormLabel'
import { Loading } from '@/components/Loading'
import { NumberInput } from '@/components/ui/NumberInput'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { InsuranceParamsProvider } from '@/context/InsuranceParamsProvider'
import { useNameof } from '@/hooks/useNameof'
import { useAddInsuranceParamsInfo } from '@/hooks/usePoleas'
import { useGetInsuranceParams } from '@/hooks/useWizard'
import { Button, FormControl, FormErrorMessage, Heading, InputGroup, InputRightElement, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, useToast } from '@chakra-ui/react'
import type { InsuranceParamsDto, PersonDto, PolicyHolderDto } from 'BrokerTsLib/src/dtos'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { AddVehicleInfoValidationRuleObject } from 'BrokerTsLib/src/validation'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useEffect } from 'react'
import * as yup from 'yup'
import { FormBody } from '../forms/FormBody'
import { FormRow } from '../forms/FormRow'
import InsureeContact from '../forms/person/InsureeContact'
import { PersonForm } from '../forms/PersonForm'
import { VehicleCategorySelection } from '../steps/vehicle/Step1/forms/VehicleForm/VehicleCategorySelection'
import { VehicleIdentificationForm } from '../steps/vehicle/Step1/forms/VehicleForm/VehicleIdentificationForm'
import { VehicleModelSelection } from '../steps/vehicle/Step1/forms/VehicleForm/VehicleModelSelection'
import { VehiclePriceSelection } from '../steps/vehicle/Step1/forms/VehicleForm/VehiclePriceSelection'

export type EditInsuranceParamsModalProps =
  {
    isOpen: boolean,
    onClose: () => void,
    onSaved?: () => void,
    insuranceParamsId: string
  }

export const EditInsuranceParamsModal = (
  props: EditInsuranceParamsModalProps
) => {
  const {
    isOpen,
    onClose
  } = props

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay
        bg='none'
        backdropFilter='auto'
        backdropInvert='30%'
        backdropBlur='5px'
      />

      <EditInsuranceParamsModalContent {...props} />
    </Modal>
  )
}

export const schema = yup.object().shape(
  {
    vehicle: yup.object().shape(AddVehicleInfoValidationRuleObject) as any
  }
) as any

const EditInsuranceParamsModalContent = ({ insuranceParamsId, onClose, onSaved }: EditInsuranceParamsModalProps) => {
  const { data, isLoading, refetch } = useGetInsuranceParams(insuranceParamsId.toString())
  const { mutateAsync: saveInsuranceParamsInfo } = useAddInsuranceParamsInfo()

  const toast = useToast()

  const methods = useForm({
    defaultValues: data?.data,
    resolver: yupResolver(schema),
    mode: 'onBlur'
  })

  const {
    setError,
    handleSubmit,
    register,
    formState: { errors },
    reset
  } = methods

  useEffect(() => {
    if (data) {
      reset(data.data)
    }
  }, [data])

  const submitForm = useCallback(handleSubmit((data) => {
    const values = cloneDeep(data)
    values.vehicle.firstRegistrationDate = formatDate(values.vehicle.firstRegistrationDate)


    saveInsuranceParamsInfo({
      paramsId: insuranceParamsId.toString(),
      data: values
    }).then(() => {
      onSaved?.()
      onClose()
      refetch() // pro pripadne opakovane budouci otevreni modalu

      toast({
        title: `Data k nabídce č. ${insuranceParamsId} byla úspěšně uložena.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    }).catch((err) => {
      if (!(err.response && err.response.status === 400 && err.response.data))
        return

      const data = err.response.data

      // zobrazeni serverove validace
      Object.keys(data).forEach((x: any) => {
        setError(global.toCamelCase(x), { message: data[x].join(' ') })
      })

    })
  }), [handleSubmit])

  const hasErrors = Object.keys(errors).length > 0

  const nameof = useNameof<InsuranceParamsDto>()
  const pnameof = useNameof<PersonDto>(nameof((x) => x.policyHolder.person))
  const phnameof = useNameof<PolicyHolderDto>(nameof((x) => x.policyHolder))

  return (
    <ModalContent maxW='760px'> {/* cca stejne jako na K1 */}
      {isLoading && <Loading large />}

      {!isLoading && data?.data &&
        <FormProvider {...methods}>
          <form onSubmit={submitForm}>
            <ModalCloseButton />

            <ModalHeader>Doplnění údajů k nabídce č. {insuranceParamsId}, LS {data.data.importedData?.leasingNumber}</ModalHeader>

            <ModalBody>
              <FormBody sx={{ gap: 8 }}>
                <InsuranceParamsProvider insuranceParamsId={insuranceParamsId}>

                  <VehicleIdentificationForm />

                  <VehicleCategorySelection />

                  <VehicleModelSelection />

                  <FormRow>

                    <FormControl isInvalid={!!errors?.vehicle?.firstRegistrationDate} isRequired>
                      <FormLabel>
                        Datum první registrace
                      </FormLabel>

                      <Datepicker name="vehicle.firstRegistrationDate" />
                    </FormControl>

                    <FormControl isInvalid={!!errors.vehicle?.mileage} isRequired>
                      <FormLabel>
                        Stav tachometru
                      </FormLabel>

                      <InputGroup>
                        <NumberInput
                          placeholder="Např. 150 000"
                          {...register('vehicle.mileage')}
                        />
                        <InputRightElement>km</InputRightElement>
                      </InputGroup>

                      {errors.vehicle?.mileage && <FormErrorMessage>{errors.vehicle.mileage.message}</FormErrorMessage>}

                    </FormControl>

                  </FormRow>

                  <VehiclePriceSelection insuranceParamsId={insuranceParamsId.toString()} />


                  <Heading size='md' fontWeight='bolder' mt={8}>Pojistník</Heading>

                  <Stack gap={12}>
                    <PersonForm
                      personPrefix={phnameof((x) => x.person)}
                      showVatPayer={true}
                    />

                    <InsureeContact personPrefix={phnameof((x) => x.person)} />
                  </Stack>

                  <ErrorSummary errors={errors} />

                </InsuranceParamsProvider>

              </FormBody>
            </ModalBody>

            <ModalFooter sx={{ justifyContent: 'center', gap: 4, mt: hasErrors ? 0 : 12 }}>
              <Button onClick={onClose}>
                Zavřít
              </Button>

              <Button colorScheme='green' onClick={submitForm}>
                Uložit
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>}
    </ModalContent>
  )
}

